import { useQuery } from '@apollo/client'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import axios from 'axios'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import Spin from 'pared/components/basicUi/spin'

import { IAiBlackboxProps } from '../../type'
import DefaultScheduleTab from './components/DefaultScheduleTab'
import ImportWizard from './components/ImportWizard'
import ScheduleDashboard from './components/ScheduleDashboard'
import {
  IDeliveryDayPrepItemsType,
  IDeliveryScheduleLocationsType,
  IDeliverySchedulesType,
  getDeliveryDayPrepItems,
  getDeliveryScheduleLocations,
  getDeliverySchedules,
} from './gql'

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 1200px;
`

const ALL_PREP_ITEMS = [
  { id: 'blue-cheese', name: 'Blue Cheese' },
  { id: 'brownies', name: 'Brownies' },
  { id: 'carrots', name: 'Carrots' },
  { id: 'celery', name: 'Celery' },
  { id: 'corn-trays', name: 'Corn Trays' },
  { id: 'garlic-sauce', name: 'Garlic Parmesan' },
  { id: 'honey-mustard', name: 'Honey Mustard' },
  { id: 'hot-honey', name: 'Hot Honey' },
  { id: 'lemon-pepper', name: 'Lemon Pepper' },
  { id: 'louisiana-rub', name: 'Louisiana Rub' },
  { id: 'mild-sauce', name: 'Mild Sauce' },
  { id: 'ranch', name: 'Ranch' },
]

const DeliveryDaySchedules: React.FC<IAiBlackboxProps> = () => {
  const [activeTab, setActiveTab] = useState(0)

  const {
    data: locationsData,
    loading,
    refetch: refetchLocations,
  } = useQuery<IDeliveryScheduleLocationsType>(getDeliveryScheduleLocations)

  // why: fetch the locations that belong to the "Prep Item Group" location-
  // group type — i.e. stores actually using prep-item / delivery-day features.
  // The previous PostGraphile `locations` query returned every row in the
  // `location` table (including admin/legacy entries), so this BE endpoint
  // replaces a FE-side numeric-code regex filter with proper group membership.
  const [prepItemLocations, setPrepItemLocations] = useState<
    Array<{ id: number; name: string; code: string }>
  >([])
  useEffect(() => {
    let cancelled = false
    axios
      .get<{ locations: Array<{ id: number; name: string; code: string }> }>(
        `${process.env.REACT_APP_BE_BASE_URL}/delivery_schedule/prep_item_locations`,
      )
      .then((resp) => {
        if (!cancelled) setPrepItemLocations(resp.data?.locations || [])
      })
      .catch(() => {
        if (!cancelled) setPrepItemLocations([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const { data: deliveryItemsData } = useQuery<IDeliveryDayPrepItemsType>(
    getDeliveryDayPrepItems,
  )

  const { data: deliverySchedulesData, refetch: refetchDeliverySchedules } =
    useQuery<IDeliverySchedulesType>(getDeliverySchedules)

  const deliverySchedules = useMemo(
    () => deliverySchedulesData?.getDeliverySchedules?.nodes ?? [],
    [deliverySchedulesData],
  )

  const locations = useMemo(
    () => locationsData?.getDeliveryScheduleLocations?.nodes ?? [],
    [locationsData],
  )

  // why: BE already filters to Prep Item Group members and ORDERs BY code,
  // so this memo is just a stable identity wrapper. Kept as a useMemo so
  // downstream `useMemo`/`useCallback` deps don't churn on every re-render.
  const allLocationOptions = useMemo(
    () => prepItemLocations,
    [prepItemLocations],
  )

  // Delivery day items -- items that stay ON during delivery days
  const currentDeliveryItems = useMemo(() => {
    if (!deliveryItemsData?.getDeliveryDayPrepItems) return new Set<string>()
    return new Set(
      deliveryItemsData.getDeliveryDayPrepItems.nodes.map((n) => n.prepItemId),
    )
  }, [deliveryItemsData])

  return (
    <Spin spinning={loading}>
      <PageContainer>
        <Alert severity="info" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            A store&rsquo;s prep days are derived from the Default Schedule, not
            copied from it.
          </Typography>
          <Typography variant="body2">
            Each store gets its own schedule, built from the Default Schedule
            and that store&rsquo;s delivery days. Two stores with different
            delivery days will be told to prep on different days. This is
            expected &mdash; a store grid that does not match the Default
            Schedule is not an error.
          </Typography>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              backgroundColor: 'transparent',
              mt: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: 0, minHeight: 0 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                How prep days are derived
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              <Typography variant="body2" component="div">
                <p>
                  <strong>Only four items move.</strong> Blue Cheese, Carrots,
                  Celery and Ranch take real prep effort, so they are never
                  scheduled on a delivery day. Every other item keeps its
                  Default Schedule days at every store.
                </p>
                <p>These four rules run in order:</p>
                <ol>
                  <li>
                    <strong>Off the delivery day.</strong> A prep day that lands
                    on a delivery day moves to the day before it. If that day is
                    also a delivery day, it steps back again.
                  </li>
                  <li>
                    <strong>No two days in a row.</strong> If the day before is
                    already next to another prep day for the same item, the item
                    goes to the least busy day that is not next to one. Ranch is
                    the exception: it already preps Friday and Saturday back to
                    back in the Default Schedule, so back to back is allowed for
                    Ranch.
                  </li>
                  <li>
                    <strong>At least two prep days.</strong> If the moves above
                    leave an item with one prep day, a second day is added.
                  </li>
                  <li>
                    <strong>At most four days apart.</strong> Two prep days more
                    than four days apart would leave a gap the first batch
                    cannot cover, so one of them moves. The gap is counted both
                    ways around the week &mdash; Sunday and Tuesday are two days
                    apart going forward but five days apart coming back, and it
                    is the five that has to fit.
                  </li>
                </ol>
                <p>
                  <strong>
                    Worked example &mdash; a Monday + Thursday store.
                  </strong>{' '}
                  Blue Cheese preps Monday and Friday in the Default Schedule.
                  Monday is a delivery day, so it moves to Sunday (rule 1).
                  Sunday and Friday are five days apart, so Friday moves back to
                  Wednesday &mdash; Tuesday would still leave five days from
                  Tuesday round to Sunday, and Wednesday is the first day that
                  keeps both stretches to four (rule 4). The store preps Blue
                  Cheese on <strong>Sunday and Wednesday</strong>. Celery preps
                  Tuesday and Thursday; Thursday is a delivery day and Wednesday
                  would sit next to Tuesday, so Celery moves to Saturday (rules
                  1 and 2).
                </p>
                <p>
                  <strong>The &#9888; badge on a store</strong> means the rules
                  could not be fully satisfied for that store &mdash; usually
                  four or more delivery days leave too few open days. The
                  schedule is still usable. To change it, either edit that
                  store&rsquo;s delivery days, or set custom delivery-day items
                  for the store so fewer items have to move.
                </p>
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Alert>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_e, v) => setActiveTab(v)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 15,
              },
            }}
          >
            <Tab label="Schedule Calendar" />
            <Tab label="Import & Setup" />
            <Tab label="Default Schedule" />
          </Tabs>
        </Box>

        {/* ========== TAB 0: Schedule Dashboard ========== */}
        {activeTab === 0 && (
          <ScheduleDashboard
            schedules={deliverySchedules}
            locations={locations}
            allLocations={allLocationOptions}
            allPrepItems={ALL_PREP_ITEMS}
            globalSafeItems={currentDeliveryItems}
            onRefresh={() => {
              refetchDeliverySchedules()
              refetchLocations()
            }}
          />
        )}

        {/* ========== TAB 1: Import & Setup ========== */}
        {activeTab === 1 && (
          <ImportWizard
            allPrepItems={ALL_PREP_ITEMS}
            globalSafeItems={currentDeliveryItems}
            allLocations={allLocationOptions}
            activeSchedules={deliverySchedules.filter(
              (s) => s.status === 'active' || s.status === 'upcoming',
            )}
            onCommitted={() => {
              refetchLocations()
              refetchDeliverySchedules()
              setActiveTab(0)
            }}
          />
        )}

        {/* ========== TAB 2: Default Schedule editor ========== */}
        {activeTab === 2 && <DefaultScheduleTab />}
      </PageContainer>
    </Spin>
  )
}

export default DeliveryDaySchedules
